import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import GenericFormDrawer from '../GenericFormDrawer';
import api from '../../services/api';
import { EXCLUSION_REASONS } from '../../pages/evaluation-periods/tab-pages/PeriodParticipantsTab';

// Spec §7.4 — muafiyet çekmecesi (İK Operasyon Panosu ve Süreç Takibi ortak; Faz 2 Plan 4 §19/9). Neden (seçim), not (`other` için
// zorunlu).
//   Tek görev (`task`): kapsam "Yalnız bu görev" → PATCH …/{id}/exemption; "Kişinin bu aşamadaki bütün görevleri" → POST …/exemptions.
//   Toplu (`tasks` dizisi, çoklu seçim): kapsam seçeneği yok → POST …/exemptions/bulk. Muaf tutulamayacak görevler (tamamlanmış,
//   iptal, zaten muaf) isteğe konmaz ve sayısı söylenir (§19/7); sunucunun hepsi-ya-da-hiçbiri kuralı değişmez.
// Hata tostu yakalayıcıda; başarıda onDone ve kapanış.
export const SCOPE_TASK = 'task';
export const SCOPE_STAGE = 'stage';

const NOT_EXEMPTABLE = ['done', 'cancelled', 'exempt'];
export const canExempt = (row) => !!row && !NOT_EXEMPTABLE.includes(row.status);
export const canRemoveExemption = (row) => !!row?.exemptAt;

export async function submitExemption(task, values) {
  const body = { reasonCode: values.reasonCode, note: values.note || null };
  try {
    if (values.scope === SCOPE_STAGE) {
      await api.post('/v1/period-stage-tasks/exemptions', { ...body, participantId: task.participantId, stageKey: task.stageKey });
    } else {
      await api.patch(`/v1/period-stage-tasks/${task.taskId}/exemption`, body);
    }
    return true;
  } catch {
    return false;
  }
}

export async function submitBulkExemption(tasks, values) {
  const taskIds = (tasks || []).filter(canExempt).map((x) => x.taskId);
  if (taskIds.length === 0) return false;
  try {
    await api.post('/v1/period-stage-tasks/exemptions/bulk', { taskIds, reasonCode: values.reasonCode, note: values.note || null });
    return true;
  } catch {
    return false;
  }
}

export default function ExemptionDrawer({ task, tasks, open, onClose, onDone }) {
  const { t } = useTranslation();
  const bulk = Array.isArray(tasks);
  const eligible = bulk ? tasks.filter(canExempt).length : 0;
  const skipped = bulk ? tasks.length - eligible : 0;
  // Görev başına sabit başlangıç değeri: yeniden çizimde yeni nesne GenericFormDrawer formunu (neden/not) sıfırlardı.
  const identity = bulk ? tasks.map((x) => x.taskId).join(',') : task?.taskId;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialValues = useMemo(() => ({ reasonCode: null, note: '', scope: SCOPE_TASK }), [identity]);
  const schema = [
    ...(bulk ? [{
      name: 'bulkNotice',
      type: 'notice',
      tone: skipped > 0 ? 'warning' : undefined,
      content: (
        <>
          <div data-bulk="eligible">{t('performance.operations.exemption.bulkCount', { count: eligible })}</div>
          {skipped > 0 ? <div data-bulk="skipped">{t('performance.operations.exemption.bulkSkipped', { count: skipped })}</div> : null}
        </>
      ),
    }] : []),
    {
      name: 'reasonCode', label: t('performance.period.participants.reason'), type: 'select', grid: 12, required: true,
      options: EXCLUSION_REASONS.map((id) => ({ id, name: t(`performance.period.participants.reasons.${id}`) })), idKey: 'id', labelKey: 'name',
    },
    {
      name: 'note', label: t('performance.operations.exemption.note'), type: 'textarea', grid: 12, maxLength: 500, rows: 3,
      required: (formData) => formData.reasonCode === 'other',
    },
    ...(bulk ? [] : [{
      name: 'scope', label: t('performance.operations.exemption.scope'), type: 'select', grid: 12, required: true,
      options: [
        { id: SCOPE_TASK, name: t('performance.operations.exemption.scopeTask') },
        { id: SCOPE_STAGE, name: t('performance.operations.exemption.scopeStage') },
      ],
      idKey: 'id', labelKey: 'name',
    }]),
  ];
  return (
    <GenericFormDrawer
      open={open}
      onClose={onClose}
      title={bulk ? t('performance.operations.exemption.bulkTitle', { count: eligible }) : t('performance.operations.exemption.title')}
      schema={schema}
      initialValues={initialValues}
      submitLabel={t('performance.operations.exemption.submit')}
      submitDisabled={bulk && eligible === 0}
      onSave={async (values) => {
        const ok = bulk ? await submitBulkExemption(tasks, values) : await submitExemption(task, values);
        if (ok) onDone?.();
        return ok;
      }}
    />
  );
}
